import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Popover,
  Button,
  Checkbox,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PaymentRow,
  PaymentMethod,
  PaymentStatus,
  createPayment,
  listPayments,
  markPaymentAsPaid,
  updatePayment,
} from '../api/payments';
import { listActivities } from '../api/activities';
import { listUsers } from '../api/users';
import { listCandidatures } from '../api/candidatures';
import { listPostsSummary } from '../api/posts';
import {
  EntityAutocomplete,
  type EntityOption,
} from '../components/EntityAutocomplete';
import { useAuth } from '../auth/AuthContext';
import { downloadRecordsCsv } from '../utils/csvExport';
import { deleteDocsInCollection } from '../api/batchDelete';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const formatAmount = (amount?: number) =>
  amount != null ? `${amount.toFixed(0)} CFA` : '—';

const statusColor = (status?: string) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'pending':
    default:
      return 'warning';
  }
};

const PAYMENT_METHOD_OPTIONS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'bank_transfer', label: 'Virement bancaire' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'cash', label: 'Especes' },
  { value: 'check', label: 'Cheque' },
  { value: 'other', label: 'Autre' },
];

const paymentMethodLabel = (method?: PaymentMethod): string =>
  PAYMENT_METHOD_OPTIONS.find((o) => o.value === method)?.label ?? (method ?? '—');

function activityDurationLabel(value?: number, unit?: string): string {
  if (value == null) return '?';
  if (unit === 'days') return `${value} j`;
  if (unit === 'months') return `${value} mois`;
  return `${value} h`;
}

export const PaymentsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canEdit = role === 'admin' || role === 'cap';
  const canBulkDeletePayments = role === 'admin';
  const { data, isLoading } = useQuery<PaymentRow[]>({
    queryKey: ['payments'],
    queryFn: () => listPayments(),
  });

  const { data: users } = useQuery({
    queryKey: ['users-for-payments'],
    queryFn: listUsers,
  });
  const { data: activities } = useQuery({
    queryKey: ['activities-for-payment-dialog'],
    queryFn: () => listActivities(),
  });
  const { data: candidatures } = useQuery({
    queryKey: ['candidatures-for-payments'],
    queryFn: listCandidatures,
  });
  const { data: posts } = useQuery({
    queryKey: ['posts-for-payments'],
    queryFn: listPostsSummary,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'all' | PaymentStatus>('all');
  const [orderBy, setOrderBy] = React.useState<
    'amount' | 'status' | 'createdAt' | 'updatedAt' | 'payer'
  >('createdAt');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');

  const [confirmAnchorEl, setConfirmAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [confirmDescription, setConfirmDescription] = React.useState('');
  const [pendingPayment, setPendingPayment] = React.useState<PaymentRow | null>(
    null
  );
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const [editPayment, setEditPayment] = React.useState<PaymentRow | null>(null);
  const [editActivityId, setEditActivityId] = React.useState('');
  const [editPaidById, setEditPaidById] = React.useState('');
  const [editMethod, setEditMethod] = React.useState<PaymentMethod>('bank_transfer');
  const [editAmount, setEditAmount] = React.useState('');
  const [editPayStatus, setEditPayStatus] = React.useState<PaymentStatus>('pending');
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [createActivityId, setCreateActivityId] = React.useState('');
  const [createPaidById, setCreatePaidById] = React.useState('');
  const [createMethod, setCreateMethod] = React.useState<PaymentMethod>('bank_transfer');
  const [createAmount, setCreateAmount] = React.useState('');
  const [createPayStatus, setCreatePayStatus] = React.useState<PaymentStatus>('pending');
  const [createSaving, setCreateSaving] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const confirmOpen = Boolean(confirmAnchorEl);

  const handleRequestSort = (
    property: 'amount' | 'status' | 'createdAt' | 'updatedAt' | 'payer'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const userNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    users?.forEach((u) => {
      const fullName = `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim();
      map[u.id] = fullName || u.email || u.id;
    });
    return map;
  }, [users]);

  const payerEditOptions = React.useMemo<EntityOption[]>(() => {
    if (!users?.length) return [];
    return [...users]
      .sort((a, b) => {
        const la = `${a.lastName ?? ''} ${a.firstName ?? ''}`.trim() || a.email || a.id;
        const lb = `${b.lastName ?? ''} ${b.firstName ?? ''}`.trim() || b.email || b.id;
        return la.localeCompare(lb, 'fr', { sensitivity: 'base' });
      })
      .map((u) => {
        const label =
          `${`${u.lastName ?? ''} ${u.firstName ?? ''}`.trim() || u.email || 'Profil'}`;
        const description =
          [u.academy, u.commune, u.speciality]
            .map((x) => (typeof x === 'string' ? x.trim() : ''))
            .filter(Boolean)
            .join(' · ') || 'Académie, commune et spécialité non renseignées';
        return { id: u.id, label, description };
      });
  }, [users]);

  const candidateNameByCandidatureId = React.useMemo(() => {
    const map: Record<string, string> = {};
    candidatures?.forEach((c) => {
      const uid = c.userId ?? '';
      if (!uid) return;
      map[c.id] = userNameById[uid] ?? uid;
    });
    return map;
  }, [candidatures, userNameById]);

  const postTitleById = React.useMemo(() => {
    const map: Record<string, string> = {};
    posts?.forEach((p) => {
      map[p.id] = p.title ?? p.id;
    });
    return map;
  }, [posts]);

  const postTitleByCandidatureId = React.useMemo(() => {
    const map: Record<string, string> = {};
    candidatures?.forEach((c) => {
      const pid = c.postId ?? '';
      if (!pid) return;
      map[c.id] = postTitleById[pid] ?? pid;
    });
    return map;
  }, [candidatures, postTitleById]);

  const activityEditOptions = React.useMemo<EntityOption[]>(() => {
    if (!activities?.length) return [];
    return activities.map((a) => {
      const d = a.date ? formatDate(a.date) : '—';
      const duration = activityDurationLabel(a.durationValue ?? a.hours, a.durationUnit);
      const candidate = a.candidatureId
        ? candidateNameByCandidatureId[a.candidatureId] ?? null
        : null;
      const postTitle = a.candidatureId
        ? postTitleByCandidatureId[a.candidatureId] ?? null
        : null;
      return {
        id: a.id,
        label: `${candidate ? `${candidate} · ` : ''}${postTitle ? `${postTitle} · ` : ''}${d} · ${duration}`,
      };
    });
  }, [activities, candidateNameByCandidatureId, postTitleByCandidatureId]);

  /** Libellé activité aligné sur le sélecteur (date · heures · extrait description). */
  const activityLabelById = React.useMemo(() => {
    const map: Record<string, string> = {};
    activityEditOptions.forEach((o) => {
      map[o.id] = o.label;
    });
    return map;
  }, [activityEditOptions]);

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((p) => {
      const payer = p.paidById ? userNameById[p.paidById] ?? p.paidById : '';
      const haystack = `${payer} ${paymentMethodLabel(p.method)}`.toLowerCase().trim();
      const matchesSearch = haystack.includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || (p.status ?? 'pending') === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter, userNameById]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      let av: string | Date | number | undefined;
      let bv: string | Date | number | undefined;

      const getPayer = (x: PaymentRow) =>
        x.paidById ? userNameById[x.paidById] ?? x.paidById : '';

      switch (orderBy) {
        case 'payer':
          av = getPayer(a).toLowerCase();
          bv = getPayer(b).toLowerCase();
          break;
        case 'status':
          av = (a.status ?? '').toLowerCase();
          bv = (b.status ?? '').toLowerCase();
          break;
        case 'amount':
          av = a.amount ?? 0;
          bv = b.amount ?? 0;
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        default:
          av = 0 as any;
          bv = 0 as any;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy, userNameById]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const allSelected =
    canEdit &&
    filtered.length > 0 &&
    filtered.every((p) => selectedIds.includes(p.id));
  const someSelected =
    canEdit &&
    filtered.length > 0 &&
    filtered.some((p) => selectedIds.includes(p.id)) &&
    !allSelected;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filtered.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleConfirmDeleteSelected = async () => {
    if (!selectedIds.length) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDocsInCollection('payments', selectedIds);
      await queryClient.invalidateQueries({ queryKey: ['payments'] });
      setSelectedIds([]);
      setDeleteDialogOpen(false);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "La suppression a échoué.");
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkPaidRequest = (
    event: React.MouseEvent<HTMLElement>,
    payment: PaymentRow
  ) => {
    const payer = payment.paidById
      ? userNameById[payment.paidById] ?? payment.paidById
      : 'cet utilisateur';
    const amountLabel = formatAmount(payment.amount);

    setConfirmDescription(
      `Confirmer le paiement de ${amountLabel} pour ${payer} ?`
    );
    setPendingPayment(payment);
    setConfirmAnchorEl(event.currentTarget);
  };

  const handleConfirmPaid = async () => {
    if (!pendingPayment) return;
    await markPaymentAsPaid(pendingPayment.id);
    await queryClient.invalidateQueries({ queryKey: ['payments'] });
    setConfirmAnchorEl(null);
    setPendingPayment(null);
  };

  const handleCancelPaid = () => {
    setConfirmAnchorEl(null);
    setPendingPayment(null);
  };

  const openEditPayment = (p: PaymentRow) => {
    setEditPayment(p);
    setEditActivityId(p.activityId ?? '');
    setEditPaidById(p.paidById ?? '');
    setEditMethod(p.method ?? 'bank_transfer');
    setEditAmount(p.amount != null ? String(p.amount) : '');
    setEditPayStatus(p.status === 'paid' ? 'paid' : 'pending');
    setEditError(null);
  };

  const closeEditPayment = () => {
    if (editSaving) return;
    setEditPayment(null);
    setEditError(null);
  };

  const openCreatePayment = () => {
    setEditPayment(null);
    setEditError(null);
    setCreateActivityId('');
    setCreatePaidById('');
    setCreateMethod('bank_transfer');
    setCreateAmount('');
    setCreatePayStatus('pending');
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreatePayment = () => {
    if (createSaving) return;
    setCreateOpen(false);
    setCreateError(null);
  };

  const handleSaveCreatePayment = async () => {
    if (!createActivityId.trim() || !createPaidById.trim()) {
      setCreateError('L’activité, le payeur et le mode de paiement sont obligatoires.');
      return;
    }
    const amountNum = Number(String(createAmount).replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(amountNum)) {
      setCreateError('Montant invalide.');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      await createPayment({
        activityId: createActivityId.trim(),
        paidById: createPaidById.trim(),
        method: createMethod,
        amount: amountNum,
        status: createPayStatus,
      });
      await queryClient.invalidateQueries({ queryKey: ['payments'] });
      setCreateOpen(false);
      setPage(0);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleSavePayment = async () => {
    if (!editPayment) return;
    if (!editActivityId.trim() || !editPaidById.trim()) {
      setEditError('L’activité, le payeur et le mode de paiement sont obligatoires.');
      return;
    }
    const amountNum = Number(String(editAmount).replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(amountNum)) {
      setEditError('Montant invalide.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updatePayment(editPayment.id, {
        activityId: editActivityId.trim(),
        paidById: editPaidById.trim(),
        method: editMethod,
        amount: amountNum,
        status: editPayStatus,
      });
      await queryClient.invalidateQueries({ queryKey: ['payments'] });
      setEditPayment(null);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleExportCsv = () => {
    const rows: Array<Record<string, unknown>> = sorted.map((p) => {
      const aid = p.activityId ?? '';
      const payerId = p.paidById ?? '';
      return {
        paymentId: p.id,
        activityId: aid,
        activityLabel: aid ? activityLabelById[aid] ?? '' : '',
        paidById: payerId,
        paidByName: payerId ? userNameById[payerId] ?? '' : '',
        method: p.method ?? '',
        methodLabel: paymentMethodLabel(p.method),
        amount: p.amount ?? '',
        status: p.status ?? '',
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
      };
    });
    downloadRecordsCsv('paiements', rows);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Paiements
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Payeur, méthode..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="paid">Payé</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 'auto' }, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreatePayment}>
              Ajouter
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            disabled={!sorted.length}
            onClick={handleExportCsv}
          >
            Exporter CSV
          </Button>
        </Box>
      </Box>
      {canEdit && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedIds.length
              ? `${selectedIds.length} sélectionné(s)`
              : 'Aucune sélection'}
          </Typography>
          {canBulkDeletePayments && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={!selectedIds.length}
              onClick={() => {
                setDeleteError(null);
                setDeleteDialogOpen(true);
              }}
            >
              Supprimer la sélection
            </Button>
          )}
        </Box>
      )}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {canEdit && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
              )}
              <TableCell sortDirection={orderBy === 'payer' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'payer'}
                  direction={orderBy === 'payer' ? order : 'asc'}
                  onClick={() => handleRequestSort('payer')}
                >
                  Payeur
                </TableSortLabel>
              </TableCell>
              <TableCell>Activité</TableCell>
              <TableCell>Méthode</TableCell>
              <TableCell sortDirection={orderBy === 'amount' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'amount'}
                  direction={orderBy === 'amount' ? order : 'asc'}
                  onClick={() => handleRequestSort('amount')}
                >
                  Montant
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créé le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mis à jour le
                </TableSortLabel>
              </TableCell>
              {canEdit && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={canEdit ? 9 : 8}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((p) => (
                <TableRow key={p.id} hover selected={canEdit && selectedIds.includes(p.id)}>
                  {canEdit && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(p.id)}
                        onChange={() => toggleOne(p.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {p.paidById
                      ? userNameById[p.paidById] ?? p.paidById
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {p.activityId
                      ? activityLabelById[p.activityId] ?? p.activityId
                      : '—'}
                  </TableCell>
                  <TableCell>{paymentMethodLabel(p.method)}</TableCell>
                  <TableCell>{formatAmount(p.amount)}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.status ?? 'pending'}
                      color={statusColor(p.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(p.createdAt)}</TableCell>
                  <TableCell>{formatDate(p.updatedAt)}</TableCell>
                  {canEdit && (
                    <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          flexWrap: 'nowrap',
                          gap: 0.25,
                        }}
                      >
                        <Tooltip title="Modifier le paiement">
                          <IconButton size="small" onClick={() => openEditPayment(p)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {p.status !== 'paid' && (
                          <Tooltip title="Marquer comme payé">
                            <IconButton
                              size="small"
                              onClick={(event) => handleMarkPaidRequest(event, p)}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={canEdit ? 9 : 8}>
                  <Typography variant="body2" color="text.secondary">
                    Aucun paiement trouvé.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100, 500]}
      />
      <Dialog open={createOpen} onClose={closeCreatePayment} maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau paiement</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {createError && (
            <Alert severity="error" onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}
          <EntityAutocomplete
            label="Activité"
            required
            options={activityEditOptions}
            valueId={createActivityId}
            onChangeId={setCreateActivityId}
            disabled={createSaving}
            loading={!activities}
            helperText="Rechercher par candidat, poste, date et durée"
            orphanLabel={createActivityId.trim() ? 'Activité (hors liste chargée)' : undefined}
          />
          <EntityAutocomplete
            label="Payeur (utilisateur)"
            required
            options={payerEditOptions}
            valueId={createPaidById}
            onChangeId={setCreatePaidById}
            disabled={createSaving}
            loading={!users}
            helperText="Rechercher par nom ou e-mail"
            orphanLabel={createPaidById.trim() ? 'Utilisateur (hors liste chargée)' : undefined}
          />
          <FormControl size="small">
            <InputLabel id="create-pay-method-label">Mode de paiement</InputLabel>
            <Select
              labelId="create-pay-method-label"
              label="Mode de paiement"
              value={createMethod}
              onChange={(e) => setCreateMethod(e.target.value as PaymentMethod)}
              disabled={createSaving}
            >
              {PAYMENT_METHOD_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Montant (CFA)"
            required
            type="text"
            value={createAmount}
            onChange={(e) => setCreateAmount(e.target.value)}
            disabled={createSaving}
          />
          <FormControl size="small">
            <InputLabel id="create-pay-status-label">Statut</InputLabel>
            <Select
              labelId="create-pay-status-label"
              label="Statut"
              value={createPayStatus}
              onChange={(e) => setCreatePayStatus(e.target.value as 'pending' | 'paid')}
              disabled={createSaving}
            >
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="paid">Payé</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreatePayment} color="inherit" disabled={createSaving}>
            Annuler
          </Button>
          <Button
            onClick={() => void handleSaveCreatePayment()}
            variant="contained"
            disabled={createSaving}
          >
            {createSaving ? 'Création…' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editPayment)} onClose={closeEditPayment} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le paiement</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {editError && (
            <Alert severity="error" onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}
          <EntityAutocomplete
            label="Activité"
            required
            options={activityEditOptions}
            valueId={editActivityId}
            onChangeId={setEditActivityId}
            disabled={editSaving}
            loading={!activities}
            helperText="Rechercher par candidat, poste, date et durée"
            orphanLabel={editActivityId.trim() ? 'Activité (hors liste chargée)' : undefined}
          />
          <EntityAutocomplete
            label="Payeur (utilisateur)"
            required
            options={payerEditOptions}
            valueId={editPaidById}
            onChangeId={setEditPaidById}
            disabled={editSaving}
            loading={!users}
            helperText="Rechercher par nom ou e-mail"
            orphanLabel={editPaidById.trim() ? 'Utilisateur (hors liste chargée)' : undefined}
          />
          <FormControl size="small">
            <InputLabel id="edit-pay-method-label">Mode de paiement</InputLabel>
            <Select
              labelId="edit-pay-method-label"
              label="Mode de paiement"
              value={editMethod}
              onChange={(e) => setEditMethod(e.target.value as PaymentMethod)}
              disabled={editSaving}
            >
              {PAYMENT_METHOD_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Montant (CFA)"
            required
            type="text"
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            disabled={editSaving}
          />
          <FormControl size="small">
            <InputLabel id="edit-pay-status-label">Statut</InputLabel>
            <Select
              labelId="edit-pay-status-label"
              label="Statut"
              value={editPayStatus}
              onChange={(e) => setEditPayStatus(e.target.value as 'pending' | 'paid')}
              disabled={editSaving}
            >
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="paid">Payé</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditPayment} color="inherit" disabled={editSaving}>
            Annuler
          </Button>
          <Button onClick={() => void handleSavePayment()} variant="contained" disabled={editSaving}>
            {editSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer les paiements sélectionnés</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedIds.length} paiement(s) seront définitivement supprimés (admin uniquement).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button onClick={handleConfirmDeleteSelected} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Suppression…' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
      <Popover
        open={canEdit && confirmOpen}
        anchorEl={confirmAnchorEl}
        onClose={handleCancelPaid}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Marquer le paiement comme payé
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {confirmDescription}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button onClick={handleCancelPaid} color="inherit" size="small">
              Annuler
            </Button>
            <Button
              onClick={handleConfirmPaid}
              color="primary"
              variant="contained"
              size="small"
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Popover>
    </Paper>
  );
};

